import { type Capability } from './permissions.js';
import { type Role } from './roles.js';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  capability: Capability;
}

/** A collapsible sidebar section (e.g. Operations) holding related nav items. */
export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

/**
 * Top-level sidebar entry: either a standalone link (Dashboard) or a group
 * with expandable children. Groups mirror the business workflow instead of
 * exposing a flat list of technical modules.
 */
export type NavSection =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; group: NavGroup };

const item = (
  id: string,
  label: string,
  path: string,
  capability: Capability,
): NavSection => ({ kind: 'item', item: { id, label, path, capability } });

const group = (id: string, label: string, items: NavItem[]): NavSection => ({
  kind: 'group',
  group: { id, label, items },
});

/**
 * Admin navigation: Dashboard, then the Operations group (exactly the five
 * core operational modules), Inventory & Expense Manager and System
 * Settings. Monitoring, accountability, finance, reconciliation, handover
 * and control-review entries are intentionally NOT listed here — their
 * pages, routes and APIs are unchanged and remain reachable through their
 * direct URLs and in-app deep links. Labels are presentation only — every
 * path is protected by its existing route guard, and every item still
 * carries the capability the shell filters on. Frontend visibility never
 * replaces backend authorization.
 */
export const ADMIN_NAV: NavSection[] = [
  item('dashboard', 'Dashboard', '/admin', 'dashboard.view'),
  group('operations', 'Operations', [
    { id: 'transactions', label: 'Record Transaction', path: '/admin/transactions', capability: 'transactions.view' },
    { id: 'washing', label: 'Wash and Dry', path: '/admin/washing', capability: 'washing.view' },
    { id: 'folding', label: 'Folding', path: '/admin/folding', capability: 'folding.view' },
    { id: 'pickup', label: 'Pick-up Transactions', path: '/admin/pickup', capability: 'transactions.view' },
    { id: 'delivery', label: 'Delivery Transactions', path: '/admin/delivery', capability: 'transactions.view' },
  ]),
  group('inventory-expense', 'Inventory & Expense Manager', [
    { id: 'inventory', label: 'Inventory', path: '/admin/inventory', capability: 'inventory.view' },
    { id: 'purchasing', label: 'Purchasing', path: '/admin/purchasing', capability: 'purchasing.view' },
    { id: 'expenses', label: 'Expenses', path: '/admin/expenses', capability: 'expenses.view' },
  ]),
  group('system', 'System Settings', [
    { id: 'users', label: 'User and Roles', path: '/admin/settings#employees', capability: 'users.read' },
    { id: 'settings', label: 'Settings', path: '/admin/settings', capability: 'settings.manage' },
    { id: 'audit', label: 'Audit Log', path: '/admin/settings#audit', capability: 'audit.read' },
    { id: 'backup', label: 'Backup and Recovery', path: '/admin/backup', capability: 'settings.manage' },
  ]),
];

/** Washing employee navigation - intentionally minimal. */
export const WASHING_NAV: NavSection[] = [
  item('dashboard', 'Dashboard', '/wash', 'dashboard.view'),
  group('operations', 'Operations', [
    { id: 'washing', label: 'Wash and Dry', path: '/wash/area', capability: 'washing.view' },
  ]),
];

/** Folding employee navigation - intentionally minimal. */
export const FOLDING_NAV: NavSection[] = [
  item('dashboard', 'Dashboard', '/fold', 'dashboard.view'),
  group('operations', 'Operations', [
    { id: 'folding', label: 'Folding', path: '/fold/area', capability: 'folding.view' },
  ]),
];

export function navForRole(role: Role): NavSection[] {
  switch (role) {
    case 'ADMIN':
      return ADMIN_NAV;
    case 'WASHING_EMPLOYEE':
      return WASHING_NAV;
    case 'FOLDING_EMPLOYEE':
      return FOLDING_NAV;
    default:
      return [];
  }
}

/** All leaf items of a section list, in sidebar order. */
export function flattenNav(sections: NavSection[]): NavItem[] {
  return sections.flatMap((section) =>
    section.kind === 'item' ? [section.item] : section.group.items,
  );
}

/** Route that each role lands on after login. */
export const HOME_PATH_FOR_ROLE: Record<Role, string> = {
  ADMIN: '/admin',
  WASHING_EMPLOYEE: '/wash',
  FOLDING_EMPLOYEE: '/fold',
};
